<template>
  <div class="meta-integration-layout">
    <!-- Sidebar -->
    <Sidebar />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <!-- Page Header -->
        <div class="page-header">
          <div class="page-title-section">
            <h1 class="page-title">Meta CAPI Integration</h1>
            <p class="page-subtitle">Connect your Meta (Facebook) Ads account to send conversion events and optimize your campaigns</p>
          </div>
        </div>

        <!-- Integration Status Card -->
        <div class="status-card" :class="{ 'connected': metaConfig.connected }">
          <div class="status-header">
            <div class="status-icon-section">
              <div class="meta-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </div>
              <div>
                <h2 class="status-title">Meta Conversions API</h2>
                <p class="status-subtitle">{{ metaConfig.connected ? 'Connected & Active' : 'Not Connected' }}</p>
              </div>
            </div>
            <div class="status-badge" :class="metaConfig.connected ? 'connected' : 'disconnected'">
              <span class="badge-dot"></span>
              {{ metaConfig.connected ? 'Connected' : 'Disconnected' }}
            </div>
          </div>

          <!-- Connected Info -->
          <div v-if="metaConfig.connected" class="connected-info">
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">Pixel ID</div>
                <div class="info-value">{{ metaConfig.config.pixel_id }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Business Account</div>
                <div class="info-value">{{ metaConfig.config.business_id }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Connected Since</div>
                <div class="info-value">{{ formatDate(metaConfig.config.connected_at) }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Status</div>
                <div class="info-value">
                  <span :class="['status-indicator', metaConfig.config.is_active ? 'active' : 'inactive']">
                    {{ metaConfig.config.is_active ? 'Active' : 'Inactive' }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Settings -->
            <div class="settings-section">
              <h3 class="settings-title">Settings</h3>
              <div class="setting-item">
                <div class="setting-info">
                  <div class="setting-name">Auto-optimize Leads</div>
                  <div class="setting-description">Automatically send lead events to Meta to improve ad targeting</div>
                </div>
                <label class="toggle-switch">
                  <input
                    type="checkbox"
                    v-model="autoOptimize"
                    @change="updateAutoOptimize"
                    :disabled="updating"
                  >
                  <span class="toggle-slider"></span>
                </label>
              </div>

              <div class="setting-item">
                <div class="setting-info">
                  <div class="setting-name">Integration Status</div>
                  <div class="setting-description">Enable or disable the Meta integration</div>
                </div>
                <label class="toggle-switch">
                  <input
                    type="checkbox"
                    v-model="isActive"
                    @change="updateActiveStatus"
                    :disabled="updating"
                  >
                  <span class="toggle-slider"></span>
                </label>
              </div>
            </div>

            <!-- Disconnect Button -->
            <div class="action-section">
              <button
                class="disconnect-button"
                @click="showDisconnectModal = true"
                :disabled="disconnecting"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M13 3h-2v10h2V3zm4.83 2.17l-1.42 1.42C17.99 7.86 19 9.81 19 12c0 3.87-3.13 7-7 7s-7-3.13-7-7c0-2.19 1.01-4.14 2.58-5.42L6.17 5.17C4.23 6.82 3 9.26 3 12c0 4.97 4.03 9 9 9s9-4.03 9-9c0-2.74-1.23-5.18-3.17-6.83z"/>
                </svg>
                {{ disconnecting ? 'Disconnecting...' : 'Disconnect Account' }}
              </button>
            </div>
          </div>

          <!-- Not Connected -->
          <div v-else class="not-connected-section">
            <div class="benefits-grid">
              <div class="benefit-item">
                <div class="benefit-icon">📊</div>
                <div class="benefit-content">
                  <h4>Better Attribution</h4>
                  <p>Track conversions accurately across devices and browsers</p>
                </div>
              </div>
              <div class="benefit-item">
                <div class="benefit-icon">🎯</div>
                <div class="benefit-content">
                  <h4>Optimize Campaigns</h4>
                  <p>Send conversion data to improve ad targeting and performance</p>
                </div>
              </div>
              <div class="benefit-item">
                <div class="benefit-icon">📈</div>
                <div class="benefit-content">
                  <h4>Increase ROAS</h4>
                  <p>Boost return on ad spend with better conversion tracking</p>
                </div>
              </div>
              <div class="benefit-item">
                <div class="benefit-icon">🔒</div>
                <div class="benefit-content">
                  <h4>Privacy Compliant</h4>
                  <p>Server-side tracking that respects user privacy</p>
                </div>
              </div>
            </div>

            <div class="connect-section">
              <button
                class="connect-button"
                @click="initiateConnection"
                :disabled="connecting"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                {{ connecting ? 'Connecting...' : 'Connect Meta Account' }}
              </button>
              <p class="connect-note">You'll be redirected to Meta to authorize the connection</p>
            </div>
          </div>
        </div>

        <!-- Info Cards -->
        <div class="info-cards-grid">
          <div class="info-card">
            <div class="info-card-header">
              <h3>What is Meta CAPI?</h3>
            </div>
            <div class="info-card-content">
              <p>The Meta Conversions API (CAPI) allows you to send web events from your server directly to Meta. This provides more reliable tracking than browser-based pixels and helps optimize your ad campaigns.</p>
            </div>
          </div>

          <div class="info-card">
            <div class="info-card-header">
              <h3>How It Works</h3>
            </div>
            <div class="info-card-content">
              <ol>
                <li>Connect your Meta Ads account</li>
                <li>Enable auto-optimize for leads</li>
                <li>Quali-D automatically sends conversion events to Meta</li>
                <li>Your ad campaigns optimize based on real conversion data</li>
              </ol>
            </div>
          </div>

          <div class="info-card">
            <div class="info-card-header">
              <h3>Requirements</h3>
            </div>
            <div class="info-card-content">
              <ul>
                <li>Active Meta Business Account</li>
                <li>Access to Meta Ads Manager</li>
                <li>Meta Pixel installed on your website</li>
                <li>Admin access to your Business Account</li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Disconnect Confirmation Modal -->
        <div v-if="showDisconnectModal" class="modal-overlay" @click="showDisconnectModal = false">
          <div class="modal-content" @click.stop>
            <div class="modal-header">
              <h3>Disconnect Meta Account?</h3>
              <button class="modal-close" @click="showDisconnectModal = false">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
            <div class="modal-body">
              <p>Are you sure you want to disconnect your Meta account? This will:</p>
              <ul>
                <li>Stop sending conversion events to Meta</li>
                <li>Disable campaign optimization features</li>
                <li>Remove your stored Meta credentials</li>
              </ul>
              <p><strong>You can reconnect at any time.</strong></p>
            </div>
            <div class="modal-footer">
              <button class="cancel-button" @click="showDisconnectModal = false">Cancel</button>
              <button class="confirm-button" @click="disconnectMeta" :disabled="disconnecting">
                {{ disconnecting ? 'Disconnecting...' : 'Yes, Disconnect' }}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '../components/Sidebar.vue'
import TopHeader from '../components/TopHeader.vue'

export default {
  name: 'MetaIntegrationView',
  components: {
    Sidebar,
    TopHeader
  },
  data () {
    return {
      metaConfig: {
        connected: false,
        config: null
      },
      autoOptimize: false,
      isActive: false,
      connecting: false,
      disconnecting: false,
      updating: false,
      showDisconnectModal: false,
      loading: true
    }
  },
  async mounted () {
    await this.loadMetaConfig()
  },
  methods: {
    async loadMetaConfig () {
      try {
        this.loading = true
        const token = localStorage.getItem('auth_token')

        const response = await fetch('https://api.quali-d.com/meta/config', {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        this.metaConfig = data

        if (data.connected && data.config) {
          this.autoOptimize = data.config.auto_optimize_leads || false
          this.isActive = data.config.is_active || false
        }
      } catch (error) {
        console.error('Error loading Meta config:', error)
        if (this.$toast) {
          this.$toast.error('Failed to load Meta configuration')
        }
      } finally {
        this.loading = false
      }
    },

    async initiateConnection () {
      try {
        this.connecting = true
        const token = localStorage.getItem('auth_token')

        const response = await fetch('https://api.quali-d.com/meta/connect', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()

        if (data.success && data.auth_url) {
          // Open Meta OAuth in new window
          window.open(data.auth_url, '_blank', 'width=600,height=700')

          // Poll for connection status
          this.pollConnectionStatus()
        } else {
          throw new Error('Failed to get authorization URL')
        }
      } catch (error) {
        console.error('Error initiating connection:', error)
        if (this.$toast) {
          this.$toast.error('Failed to initiate Meta connection')
        }
        this.connecting = false
      }
    },

    pollConnectionStatus () {
      const pollInterval = setInterval(async () => {
        await this.loadMetaConfig()

        if (this.metaConfig.connected) {
          clearInterval(pollInterval)
          this.connecting = false
          if (this.$toast) {
            this.$toast.success('Meta account connected successfully!')
          }
        }
      }, 3000)

      // Stop polling after 5 minutes
      setTimeout(() => {
        clearInterval(pollInterval)
        this.connecting = false
      }, 300000)
    },

    async updateAutoOptimize () {
      await this.updateConfig({ auto_optimize_leads: this.autoOptimize })
    },

    async updateActiveStatus () {
      await this.updateConfig({ is_active: this.isActive })
    },

    async updateConfig (updates) {
      try {
        this.updating = true
        const token = localStorage.getItem('auth_token')

        const response = await fetch('https://api.quali-d.com/meta/config', {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updates)
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()

        if (data.success) {
          if (this.$toast) {
            this.$toast.success('Settings updated successfully')
          }
          await this.loadMetaConfig()
        }
      } catch (error) {
        console.error('Error updating config:', error)
        if (this.$toast) {
          this.$toast.error('Failed to update settings')
        }
        // Revert changes
        await this.loadMetaConfig()
      } finally {
        this.updating = false
      }
    },

    async disconnectMeta () {
      try {
        this.disconnecting = true
        const token = localStorage.getItem('auth_token')

        const response = await fetch('https://api.quali-d.com/meta/disconnect', {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()

        if (data.success) {
          if (this.$toast) {
            this.$toast.success('Meta account disconnected successfully')
          }
          this.showDisconnectModal = false
          await this.loadMetaConfig()
        }
      } catch (error) {
        console.error('Error disconnecting Meta:', error)
        if (this.$toast) {
          this.$toast.error('Failed to disconnect Meta account')
        }
      } finally {
        this.disconnecting = false
      }
    },

    formatDate (dateString) {
      if (!dateString) return 'Unknown'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
.meta-integration-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-primary);
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 280px;
  min-height: 100vh;
}

.page-content {
  flex: 1;
  padding: 32px;
  background: var(--bg-secondary);
}

/* Page Header */
.page-header {
  margin-bottom: 32px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}

.page-subtitle {
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0;
  max-width: 700px;
}

/* Status Card */
.status-card {
  background: var(--bg-primary);
  border: 2px solid var(--border-primary);
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 32px;
  box-shadow: 0 1px 3px var(--shadow-primary);
  transition: all 0.3s ease;
}

.status-card.connected {
  border-color: #10b981;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, var(--bg-primary) 100%);
}

.status-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid var(--border-primary);
}

.status-icon-section {
  display: flex;
  align-items: center;
  gap: 20px;
}

.meta-icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: linear-gradient(135deg, #1877f2 0%, #0c5ecf 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 8px 24px rgba(24, 119, 242, 0.3);
}

.status-title {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 4px 0;
}

.status-subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}

.status-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 24px;
  font-size: 14px;
  font-weight: 600;
}

.status-badge.connected {
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
}

.status-badge.disconnected {
  background: rgba(100, 116, 139, 0.1);
  color: #64748b;
}

.badge-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* Connected Info */
.connected-info {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 24px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-secondary);
}

.info-value {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

.status-indicator {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.status-indicator.active {
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
}

.status-indicator.inactive {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

/* Settings Section */
.settings-section {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  padding: 24px;
}

.settings-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 20px 0;
}

.setting-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid var(--border-primary);
}

.setting-item:last-child {
  border-bottom: none;
}

.setting-info {
  flex: 1;
}

.setting-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
}

.setting-description {
  font-size: 13px;
  color: var(--text-secondary);
}

/* Toggle Switch */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 52px;
  height: 28px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--border-primary);
  transition: 0.3s;
  border-radius: 28px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
}

input:checked + .toggle-slider {
  background-color: #10b981;
}

input:checked + .toggle-slider:before {
  transform: translateX(24px);
}

input:disabled + .toggle-slider {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Action Section */
.action-section {
  display: flex;
  justify-content: flex-end;
  padding-top: 24px;
  border-top: 1px solid var(--border-primary);
}

.disconnect-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.disconnect-button:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.2);
  border-color: #dc2626;
}

.disconnect-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Not Connected Section */
.not-connected-section {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.benefits-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.benefit-item {
  display: flex;
  gap: 16px;
}

.benefit-icon {
  font-size: 32px;
  flex-shrink: 0;
}

.benefit-content h4 {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px 0;
}

.benefit-content p {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}

.connect-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 32px;
  background: var(--bg-secondary);
  border: 2px dashed var(--border-primary);
  border-radius: 12px;
}

.connect-button {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 32px;
  background: linear-gradient(135deg, #1877f2 0%, #0c5ecf 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 12px rgba(24, 119, 242, 0.3);
}

.connect-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(24, 119, 242, 0.4);
}

.connect-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.connect-note {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
  text-align: center;
}

/* Info Cards */
.info-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
}

.info-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px var(--shadow-primary);
}

.info-card-header h3 {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 16px 0;
}

.info-card-content {
  color: var(--text-secondary);
  line-height: 1.6;
}

.info-card-content p {
  margin: 0;
}

.info-card-content ol,
.info-card-content ul {
  margin: 0;
  padding-left: 20px;
}

.info-card-content li {
  margin-bottom: 8px;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: var(--bg-primary);
  border-radius: 16px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 16px;
  border-bottom: 1px solid var(--border-primary);
}

.modal-header h3 {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 4px;
  transition: color 0.2s;
}

.modal-close:hover {
  color: var(--text-primary);
}

.modal-body {
  padding: 24px;
  color: var(--text-secondary);
}

.modal-body p {
  margin: 0 0 16px 0;
}

.modal-body ul {
  margin: 0 0 16px 0;
  padding-left: 20px;
}

.modal-body li {
  margin-bottom: 8px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px 24px;
}

.cancel-button {
  padding: 10px 20px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-button:hover {
  background: var(--bg-tertiary);
}

.confirm-button {
  padding: 10px 20px;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.confirm-button:hover:not(:disabled) {
  background: #b91c1c;
}

.confirm-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Responsive */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
  }

  .page-content {
    padding: 16px;
  }

  .status-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .benefits-grid {
    grid-template-columns: 1fr;
  }
}
</style>
